import type { DifficultyBand } from './bands';
import type { FixtureCellData } from '../constants';

export type FixtureLeg = {
  fixture: FixtureCellData;
  valueLabel: string;
  band: DifficultyBand;
};

type AnalyserFixtureCellProps = {
  legs: FixtureLeg[];
};

const FULL_LIKELIHOOD = 1;
const SINGLE_LEG_MIN_HEIGHT = 42;
const DOUBLE_LEG_MIN_HEIGHT = 19;

const Leg = ({
  fixture,
  valueLabel,
  band,
  minHeight
}: FixtureLeg & {
  minHeight: number;
}) => {
  const venueLetter = fixture.venue === 'home' ? 'H' : 'A';
  const toPlay = fixture.inferences?.probabilities.toPlay ?? FULL_LIKELIHOOD;
  const isDoubtful = toPlay !== FULL_LIKELIHOOD;

  return (
    <div
      className="relative flex flex-1 items-center justify-between gap-1.5 rounded-lg px-2"
      style={{
        minHeight,
        backgroundColor: band.background,
        borderLeft: `3px solid ${band.bar}`
      }}
    >
      {isDoubtful && (
        <div className="bg-diagonal-stripes pointer-events-none absolute inset-0 rounded-lg opacity-50" />
      )}
      <span className="relative flex items-baseline gap-1 whitespace-nowrap">
        <span className="text-[12.5px] font-semibold text-neutral-800">
          {fixture.opponent.shortName}
        </span>
        <span className="font-open-sans text-[10px] font-semibold text-neutral-600">
          {venueLetter}
        </span>
      </span>
      <span
        data-testid="fixture-cell-value"
        className="relative text-[11.5px] font-semibold tabular-nums"
        style={{ color: band.text }}
      >
        {valueLabel}
      </span>
    </div>
  );
};

export const AnalyserFixtureCell = ({ legs }: AnalyserFixtureCellProps) => {
  const minHeight =
    legs.length > 1 ? DOUBLE_LEG_MIN_HEIGHT : SINGLE_LEG_MIN_HEIGHT;

  return (
    <div className="flex h-full w-full flex-col gap-[3px]">
      {legs.map((leg) => (
        <Leg
          key={`${leg.fixture.gameweek}-${leg.fixture.opponent.shortName}-${leg.fixture.venue}`}
          {...leg}
          minHeight={minHeight}
        />
      ))}
    </div>
  );
};
